<template>
  <div class="landing-new__body">
    <section class="questions" aria-labelledby="faq-heading">
      <h2 id="faq-heading">{{ faq.heading }}</h2>
      <p>{{ faq.description }}</p>
      <div class="questions__body">
        <ul>
          <li
            v-for="(item, index) in items"
            :key="item.question"
            :class="{ active: openIndex === index }"
          >
            <h3
              role="button"
              tabindex="0"
              :aria-expanded="openIndex === index"
              @click="toggle(index)"
              @keydown="handleKeyDown($event, index)"
            >
              {{ item.question }} <span aria-hidden="true" />
            </h3>
            <p>
              <slot name="answer" :item="item">
                {{ item.answer }}
                <template v-if="item.demoLabel">
                  <a
                    :href="demoUrl"
                    :data-tf-popup="demoFormId"
                    :data-tf-iframe-props="`title=${$t('try')}`"
                  >
                    {{ item.demoLabel }}
                  </a>
                  {{ item.demoAfter }}
                </template>
              </slot>
            </p>
          </li>
        </ul>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  props: {
    content: {
      type: Object,
      default: null,
    },
    limit: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      openIndex: null,
    };
  },
  computed: {
    defaultItems() {
      const items = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13].map((n) => ({
        question: this.$t(`home.faq.q${n}`),
        answer: this.$t(`home.faq.a${n}`),
      }));
      items[0].demoLabel = this.$t('home.faq.demoLabel');
      items[0].demoAfter = this.$t('home.faq.demoAfter');
      return items;
    },
    faq() {
      return (
        this.content || {
          heading: this.$t('home.faq.heading'),
          description: this.$t('home.faq.description'),
          items: this.defaultItems,
        }
      );
    },
    demoFormId() {
      return this.$i18n.locale === 'pl' ? 'iBhXNckk' : 'CctmLklr';
    },
    demoUrl() {
      return `https://u45ibh2u9cs.typeform.com/to/${this.demoFormId}`;
    },
    items() {
      return this.limit ? this.faq.items.slice(0, this.limit) : this.faq.items;
    },
  },
  methods: {
    toggle(index) {
      this.openIndex = this.openIndex === index ? null : index;
    },
    handleKeyDown(event, index) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.toggle(index);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.questions {
  margin: 0 auto;
  padding-bottom: 80px;
  padding-top: 80px;
  text-align: center;
  width: 1010px;

  > h2 {
    color: #1b2733;
    font-size: 31px;
    font-weight: 700;
    padding: 0 10px;
    text-align: center;
  }

  > p {
    color: rgba(27, 39, 51, 0.7);
    font-size: 14px;
    line-height: 1.6;
    margin-bottom: 60px;
    margin-top: 12px;
    text-align: center;
  }
}

.questions__body {
  text-align: left;

  ul li {
    --faq-padding-block: 20px;
    --faq-padding-inline: 10px;

    border-top: 1px solid rgba(27, 39, 51, 0.1);

    &:last-child {
      border-bottom: 1px solid rgba(27, 39, 51, 0.1);
    }

    p {
      color: #1b2733;
      cursor: text;
      display: none;
      font-size: 14px;
      line-height: 1.57;
      margin: 0 var(--faq-padding-inline) var(--faq-padding-block);
      max-width: calc(100% - 2 * var(--faq-padding-inline));
      white-space: pre-line;
      width: 500px;

      a {
        border-bottom: 1px solid rgba(42, 138, 234, 0.2);
        color: rgba(42, 138, 234, 0.85);

        &:hover {
          border-bottom-color: rgba(42, 138, 234, 0.4);
          color: #2a8aea;
        }
      }
    }

    h3 {
      align-items: center;
      color: #1b2733;
      cursor: pointer;
      display: flex;
      font-size: 21px;
      font-weight: 400;
      justify-content: space-between;
      margin: 0;
      padding: var(--faq-padding-block) calc(var(--faq-padding-inline) + 25px)
        var(--faq-padding-block) var(--faq-padding-inline);
      position: relative;
      user-select: none;
      line-height: 1.25;

      span {
        display: inline-block;
        height: 20px;
        margin-right: -26px;
        position: relative;
        transition: all 0.5s cubic-bezier(0.19, 1, 0.22, 1);
        width: 20px;

        &::before,
        &::after {
          background-color: #2a8aea;
          bottom: 0;
          content: '';
          display: block;
          height: 16px;
          margin: auto;
          position: absolute;
          right: 9px;
          top: 0;
          width: 2px;
        }

        &::after {
          transform: rotate(90deg);
        }
      }
    }

    &.active h3 {
      padding-bottom: 10px;
    }

    &.active p {
      display: inline-block;
    }

    &.active h3 span {
      transform: rotate(45deg);
    }
  }
}

@media (max-width: 1024px) {
  .questions {
    width: 100%;
  }
}

@media (max-width: 767px) {
  .questions {
    padding-top: 56px;
  }

  .questions__body ul li {
    --faq-padding-block: 19px;
    --faq-padding-inline: 16px;

    p {
      width: 100%;
    }

    h3 {
      font-size: 17px;
      font-weight: 500;
      line-height: 1.59;
    }
  }
}

@media (max-width: 413px) {
  .questions > h2 {
    font-size: 27px;
  }
  .questions__body ul li {
    --faq-padding-inline: 10px;
  }
}
</style>
